import IAuth0User from "../auth0/IAuth0User";
import IUsuarioDrupal from "../drupal/IUsuarioDrupal";
import NSNodeCompany from "../drupal/INodeCompany";

/**
 * Unified user interface that bridges Auth0 and legacy Drupal user structures
 */
export interface IAppUser {
    // Core user properties
    id: string; // Maps to Auth0 sub or Drupal uid
    email: string;
    name: string;
    roles: string[];
    
    // Profile information
    firstName?: string;
    lastName?: string;
    picture?: string;
    
    // Contact information
    phone?: string;
    mobilePhone?: string;
    
    // Location information
    country?: string;
    location?: string;
    
    // Company information
    company?: NSNodeCompany.INodeCompany;
    jobTitle?: string;
    
    // Timestamps
    created?: number;
    changed?: number;
    
    // Original user objects for backward compatibility
    auth0User?: IAuth0User;
    drupalUser?: IUsuarioDrupal;
}

/**
 * Convert Auth0 user to unified app user
 */
export function auth0ToAppUser(auth0User: IAuth0User): IAppUser {
    return {
        id: auth0User.sub,
        email: auth0User.email,
        name: auth0User.name,
        roles: auth0User.roles || [],
        
        firstName: auth0User.given_name,
        lastName: auth0User.family_name,
        picture: auth0User.picture,
        
        phone: auth0User.phone,
        mobilePhone: auth0User.phone_mobile,
        
        country: auth0User.address_country,
        location: auth0User.address_location,
        
        jobTitle: auth0User.job_title,
        
        auth0User: auth0User
    };
}

/**
 * Convert Drupal user to unified app user
 */
export function drupalToAppUser(drupalUser: IUsuarioDrupal): IAppUser {
    return {
        id: drupalUser.uid.toString(),
        email: drupalUser.mail,
        name: drupalUser.name,
        roles: drupalUser.roles,
        
        firstName: drupalUser.field_user_firstname,
        lastName: drupalUser.field_user_lastname,
        picture: drupalUser.user_picture_url || drupalUser.user_picture_base64,
        
        phone: drupalUser.field_user_phone,
        mobilePhone: drupalUser.field_user_mobilephone,
        
        country: drupalUser.field_user_country,
        location: drupalUser.field_user_location,
        
        company: drupalUser.field_user_company,
        jobTitle: drupalUser.field_user_jobtitle,
        
        created: drupalUser.created,
        changed: drupalUser.changed,
        
        drupalUser: drupalUser
    };
}

/**
 * Create a minimal IUsuarioDrupal from Auth0User for backward compatibility
 */
export function auth0ToDrupalUser(auth0User: IAuth0User): IUsuarioDrupal {
    return {
        uid: 0, // Auth0 doesn't have numeric IDs
        uuid: auth0User.sub,
        name: auth0User.name,
        mail: auth0User.email,
        roles: auth0User.roles || [],
        created: 0,
        changed: 0,
        
        field_user_firstname: auth0User.given_name,
        field_user_lastname: auth0User.family_name,
        field_user_phone: auth0User.phone,
        field_user_mobilephone: auth0User.phone_mobile,
        field_user_country: auth0User.address_country,
        field_user_location: auth0User.address_location,
        field_user_jobtitle: auth0User.job_title,
        
        user_picture_url: auth0User.picture
    };
}

export default IAppUser;